import type { SfContactResponse } from '@features/salesforce-redirect/api/get-sf-contact/getSfContact.types';
import type { SfAccount } from '@features/salesforce-redirect/api/types/sfRedirect.types';

import type {
  SalesforceParams,
  SalesforceRedirectState,
} from './useSalesforceRedirect.types';

/**
 * Parse and validate Salesforce redirect query parameters.
 * Both sfaccountid and sfmastercontactid are required.
 */
export function parseSalesforceParams(
  searchParams: URLSearchParams
): SalesforceParams | null {
  const sfAccountId = searchParams.get('sfaccountid');
  const sfMasterContactId = searchParams.get('sfmastercontactid');

  if (!sfAccountId || !sfMasterContactId) {
    return null;
  }

  return { sfAccountId, sfMasterContactId };
}

const REQUIRED_PERMISSIONS = ['Users_Add_Org', 'UsersOrganization_Add_All'];

/**
 * Check if user has all required SF redirect permissions.
 */
export function hasRequiredPermissions(permissionNames: string[]): boolean {
  return REQUIRED_PERMISSIONS.every(required =>
    permissionNames.includes(required)
  );
}

export interface ComputeSalesforceRedirectStateInput {
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  params: SalesforceParams | null;
  permissionsGranted: boolean | null;
  contactData: SfContactResponse | undefined;
  contactIsError: boolean;
  discrepancyResolved: boolean;
  isSyncing: boolean;
  orgData: SfAccount | undefined;
  orgIsError: boolean;
  isImportPending: boolean;
  isImportSuccess: boolean;
  isImportError: boolean;
  orgError: boolean;
}

function resolveDiscrepancyState(
  contactResponse: SfContactResponse,
  isSyncing: boolean
): SalesforceRedirectState {
  if (contactResponse.discrepancyType === 'SFDuplication') {
    return { step: 'discrepancy-sf-duplication', contactResponse };
  }
  if (contactResponse.discrepancyType === 'EmailDuplication') {
    return { step: 'discrepancy-email-duplication', contactResponse };
  }
  if (contactResponse.discrepancyType === 'EmailDiscrepancy') {
    return { step: 'discrepancy-email', contactResponse };
  }
  return { step: 'discrepancy-name', contactResponse, isSyncing };
}

/**
 * Pure state machine for the Salesforce redirect flow.
 * Extracted from the hook so it can be unit-tested without hook infrastructure.
 */
export function computeSalesforceRedirectState(
  input: ComputeSalesforceRedirectStateInput
): SalesforceRedirectState {
  const {
    isAuthLoading,
    isAuthenticated,
    params,
    permissionsGranted,
    contactData,
    contactIsError,
    discrepancyResolved,
    isSyncing,
    orgData,
    orgIsError,
    isImportPending,
    isImportSuccess,
    isImportError,
    orgError,
  } = input;

  if (isAuthLoading || !isAuthenticated) return { step: 'loading' };

  if (!params) {
    return {
      step: 'invalid-params',
    };
  }

  if (permissionsGranted === null) return { step: 'loading' };
  if (permissionsGranted === false) return { step: 'no-permissions' };

  if (contactIsError) return { step: 'load-error' };
  if (!contactData) return { step: 'loading' };

  if (contactData.discrepancyType !== 'None' && !discrepancyResolved) {
    return resolveDiscrepancyState(contactData, isSyncing);
  }

  if (orgIsError) return { step: 'load-error' };
  if (!orgData) return { step: 'loading' };

  if (!orgData.isOnboarded && !isImportSuccess) {
    if (isImportPending)
      return {
        step: 'importing-org',
        org: orgData,
        contactResponse: contactData,
      };
    if (isImportError) return { step: 'org-create-error', org: orgData };
    return {
      step: 'org-not-onboarded',
      org: orgData,
      contactResponse: contactData,
    };
  }

  if (orgError) return { step: 'load-error' };

  return { step: 'loading' };
}
